import { ActivityType } from '@common-fe/common-fe';

import {
  IClaimPostingInfoDtoTransactionTypeEnum,
  ILetterOfMedicalNecessityDto,
} from '@/dtos';

export enum TransactionStatusKeys {
  Processing = 'PROCESSING',
  Reprocessing = 'REPROCESSING',
  NeedsHelp = 'NEEDS_HELP',
  Paid = 'PAID',
  PaidIneligibleExpense = 'PAID_INELIGIBLE_EXPENSE',
  Error = 'ERROR',
  PartiallyPaid = 'PARTIALLY_PAID',
  PartiallyApproved = 'PARTIALLY_APPROVED',
  RepaymentRequired = 'REPAYMENT_REQUIRED',
  WaitingForFunds = 'WAITING_FOR_FUNDs',
  Pending = 'PENDING',
  PendingReview = 'PENDING_REVIEW',
  Repaid = 'REPAID',
  Denied = 'DENIED',
  Approved = 'APPROVED',
  LoA = 'LOA',
  Expired = 'EXPIRED',
  Uploading = 'UPLOADING',
  Complete = 'COMPLETE',
  Declined = 'DECLINED',
  Cancelled = 'CANCELLED',
  DocsNeeded = 'DOCS_NEEDED',
  Retirement = 'RETIREMENT',
  Future = 'FUTURE',
  OutOfPocket = 'OUT_OF_POCKET',
  Locked = 'LOCKED',
  Terrminated = 'TERRMINATED',
  AppliedToDeductible = 'APPLIED_TO_DEDUCTIBLE',
  Voided = 'VOIDED',
  StopProcessing = 'STOP_PROCESSING',
  PendingAccountVerification = 'PENDING_ACCOUNT_VERIFICATION',
  AccountVerificationFailed = 'VERIFICATION_FAILED',
  PartiallyRepaid = 'PARTIALLY_REPAID',
}
export enum TransactionStatus {
  PaidIneligibleExpense = 'Paid ineligible expense',
  Processing = 'Processing',
  Reprocessing = 'Reprocessing',
  NeedsHelp = 'Needs help',
  Paid = 'Paid',
  Error = 'Error',
  PartiallyPaid = 'Partially paid',
  PartiallyApproved = 'Partially approved',
  RepaymentRequired = 'Repayment Required',
  RepaymentInitiated = 'Repayment Initiated',
  PartiallyRepaid = 'Partially Repaid',
  WaitingForFunds = 'Waiting for funds',
  Pending = 'Pending',
  PendingReview = 'Pending review',
  Repaid = 'Repaid',
  Denied = 'Denied',
  Approved = 'Approved',
  LoA = 'LoA',
  Expired = 'Expired',
  Uploading = 'Uploading',
  Complete = 'Complete',
  Declined = 'Declined',
  Cancelled = 'Cancelled',
  DocsNeeded = 'Docs needed',
  Retirement = 'Retirement',
  Future = 'Future',
  OutOfPocket = 'Out of pocket',
  Locked = 'Locked',
  Terrminated = 'Terrminated',
  AppliedToDeductible = 'Applied to deductible',
  Voided = 'Voided',
  StopProcessing = 'Stop processing',
  PendingAccountVerification = 'Pending account verification',
  AccountVerificationFailed = 'Account verification failed',
}

export enum PaymentType {
  REIMBURSEMENT = 'REIMBURSEMENT',
  BILL_PAY = 'BILL_PAY',
  CARD_SWIPE = 'CARD_SWIPE',
  REPAYMENT = 'REPAYMENT',
}

export interface PostingFee {
  accountId?: string;
  accountName?: string;
  amount?: string;
  claimId?: string;
  feeId?: string;
  feeType?: string;
  paymentFeeType?: string;
  policyId?: string;
  postingId?: string;
  transactionAt?: string;
  date?: string;
}
export interface PostingFeePayload {
  account_id?: number;
  account_name?: string;
  amount?: number;
  claim_id?: number;
  fee_id?: number;
  fee_type?: string;
  payment_fee_type?: string;
  policy_id?: number;
  posting_id?: number;
  transaction_at?: string;
}
export interface NonClaimTransactionResponse {
  amount: number;
  category: ActivityType;
  employee_id: number;
  memo: string;
  plan_name: string;
  posting_id: number;
  status: TransactionStatus;
  status_date: string;
  transaction_type: TransactionType;
  payment_method_id: string;
  tax_year?: string;
  fees?: PostingFeePayload[];
  fee_modifications?: FeeModificationPayload[];
  activity_details_v2?: PreFormattedDataPayload[];
  status_details?: PreFormattedDataPayload[];
}

export interface NonClaimTransactionData {
  amount: number;
  category: ActivityType;
  employeeId: number;
  memo: string;
  planName: string;
  postingId: number;
  statuses: Status[];
  statusDate: Date | string;
  transactionType: TransactionType;
  paymentType?: PaymentType;
  paymentMethodId?: string;
  taxYear?: string;
  paymentFees?: PostingFee[];
  feeModifications?: FeeModification[];
  preFormattedActivityDetails?: PreFormattedData[];
  service?: Service;
}

export enum TransactionStatusFilterOptions {
  PROCESSING = 'PROCESSING',
  NEEDS_HELP = 'NEEDS_HELP',
  PAID = 'PAID',
  PARTIALLY_PAID = 'PARTIALLY_PAID',
  WAITING_FOR_FUNDS = 'WAITING_FOR_FUNDS',
  PENDING = 'PENDING',
  REPAID = 'REPAID',
  DENIED = 'DENIED',
  EXPIRED = 'EXPIRED',
}

export enum TransactionType {
  PAY_CLAIM = 'PAY_CLAIM',
  CARD_SETTLEMENT = 'CARD_SETTLEMENT',
  NOTIONAL_FUND = 'NOTIONAL_FUND',
  CHECK_CLEARED = 'CHECK_CLEARED',
  CONTRIBUTION_PAYMENT = 'CONTRIBUTION_PAYMENT',
  PAYROLL = 'PAYROLL',
  INTEREST = 'INTEREST',
  FEE = 'FEE',
  PAYMENT = 'PAYMENT',
  TRANSFER_ADJUSMENT = 'TRANSFER_ADJUSMENT',
  INVESTMENT = 'INVESTMENT',
  NOTIONAL_PAYROLL = 'NOTIONAL_PAYROLL',
  BUY_INVESTMENT = 'BUY_INVESTMENT',
  SELL_INVESTMENT = 'SELL_INVESTMENT',
  ROLLOVER = 'ROLLOVER',
  PAYMENT_CARD_RETURN = 'PAYMENT_CARD_RETURN',
  TRANSFER_OF_ASSETS = 'TRANSFER_OF_ASSETS',
  INTERNAL_TRANSFER_OF_ASSETS = 'INTERNAL_TRANSFER_OF_ASSETS',
  OTHER_ROOT_INTERNAL_TRANSFER_OF_ASSETS = 'OTHER_ROOT_INTERNAL_TRANSFER_OF_ASSETS',
  INBOUND_TRANSFER_OF_ASSETS = 'INBOUND_TRANSFER_OF_ASSETS',
  OUTBOUND_TRANSFER_OF_ASSETS = 'OUTBOUND_TRANSFER_OF_ASSETS',
  CLAIM_REPAYMENT = 'CLAIM_REPAYMENT',
  HSA_INCOMING_CONTRIBUTION = 'HSA_INCOMING_CONTRIBUTION',
  HSA_ROLLOVER = 'HSA_ROLLOVER',
  HSA_INCOMING_CONTRIBUTION_REVERSE = 'HSA_INCOMING_CONTRIBUTION_REVERSE',
  HSA_ROLLOVER_REVERSE = 'HSA_ROLLOVER_REVERSE',
  PAYROLL_REVERSE = 'PAYROLL_REVERSE',
  FORFEITURE = 'FORFEITURE',
  PAYMENT_EE_FEE = 'PAYMENT_EE_FEE',
  ACCOUNT_MAINTENANCE_EE_FEE = 'ACCOUNT_MAINTENANCE_EE_FEE',
  PAPER_STATEMENT_FEE = 'PAPER_STATEMENT_FEE',
  FEE_TRANSFER_INTERNAL = 'FEE_TRANSFER_INTERNAL',
  FEE_TRANSFER = 'FEE_TRANSFER',
  PAPER_STATEMENT_EE_FEE = 'PAPER_STATEMENT_EE_FEE',
  NOTIONAL_PAYROLL_REVERSE = 'NOTIONAL_PAYROLL_REVERSE',
  RECORD_KEEPING_PAYROLL = 'RECORD_KEEPING_PAYROLL',
}
export const TRANSACTION_TYPE_TITLES: Record<string, string> = {
  [IClaimPostingInfoDtoTransactionTypeEnum.NOTIONAL_FUND]: 'Notional Fund',
  [IClaimPostingInfoDtoTransactionTypeEnum.REPLENISHMENT_PAYMENT]: 'Replenishment Payment',
  [IClaimPostingInfoDtoTransactionTypeEnum.REPLENISHMENT]: 'Replenishment',
  [IClaimPostingInfoDtoTransactionTypeEnum.NOTIONAL_PAYROLL]: 'Notional Payroll',
  [IClaimPostingInfoDtoTransactionTypeEnum.CARD_SETTLEMENT]: 'Card Settlement',
  [IClaimPostingInfoDtoTransactionTypeEnum.PAY_CLAIM]: 'Pay Claim',
  [IClaimPostingInfoDtoTransactionTypeEnum.CHECK_CLEARED]: 'Check Cleared',
  [IClaimPostingInfoDtoTransactionTypeEnum.CARD_PROCESSOR_PAYMENT]: 'Card Processor Payment',
  [IClaimPostingInfoDtoTransactionTypeEnum.RECORD_KEEPING_PAYROLL]: 'Record Keeping Payroll',
  [IClaimPostingInfoDtoTransactionTypeEnum.CONTRIBUTION_PAYMENT]: 'Contribution Payment',
  [IClaimPostingInfoDtoTransactionTypeEnum.PAYROLL]: 'Payroll',
  [IClaimPostingInfoDtoTransactionTypeEnum.EMPLOYER_PAYMENT]: 'Employer Payment',
  [IClaimPostingInfoDtoTransactionTypeEnum.PAYMENT]: 'Payment',
  [IClaimPostingInfoDtoTransactionTypeEnum.INTEREST]: 'Interest',
  [IClaimPostingInfoDtoTransactionTypeEnum.FEE]: 'Fee',
  [IClaimPostingInfoDtoTransactionTypeEnum.TRANSFER_OF_ASSETS]: 'Transfer of Assets',
  [IClaimPostingInfoDtoTransactionTypeEnum.INVESTMENT]: 'Investment',
  [IClaimPostingInfoDtoTransactionTypeEnum.ACH_CLEARED]: 'ACH Cleared',
  [IClaimPostingInfoDtoTransactionTypeEnum.DEBIT_CLEARED]: 'Debit Cleared',
  [IClaimPostingInfoDtoTransactionTypeEnum.VENMO_CLEARED]: 'Venmo Cleared',
  [IClaimPostingInfoDtoTransactionTypeEnum.PAYPAL_CLEARED]: 'PayPal Cleared',
  [IClaimPostingInfoDtoTransactionTypeEnum.ADJUSTMENT]: 'Adjustment',
  [IClaimPostingInfoDtoTransactionTypeEnum.INTEREST_PAYMENT]: 'Interest Payment',
  [IClaimPostingInfoDtoTransactionTypeEnum.BUY_INVESTMENT]: 'Buy Investment',
  [IClaimPostingInfoDtoTransactionTypeEnum.SELL_INVESTMENT]: 'Sell Investment',
  [IClaimPostingInfoDtoTransactionTypeEnum.BUY_INVESTMENT_CLEARED]: 'Buy Investment Cleared',
  [IClaimPostingInfoDtoTransactionTypeEnum.SELL_INVESTMENT_CLEARED]: 'Sell Investment Cleared',
  [IClaimPostingInfoDtoTransactionTypeEnum.CLAIM_REPAYMENT]: 'Claim Repayment',
  [IClaimPostingInfoDtoTransactionTypeEnum.LOCKBOX_PAYMENT]: 'Lockbox Payment',
  [IClaimPostingInfoDtoTransactionTypeEnum.PAYMENT_CARD_RETURN]: 'Payment Card Return',
  [IClaimPostingInfoDtoTransactionTypeEnum.ROLLOVER]: 'Rollover',
  [IClaimPostingInfoDtoTransactionTypeEnum.DIGITAL_SETTLEMENT]: 'Digital Settlement',
  [IClaimPostingInfoDtoTransactionTypeEnum.DEBIT_VENMO_PAYPAL_REJECTED]: 'Debit Venmo PayPal Rejected',
  [IClaimPostingInfoDtoTransactionTypeEnum.RETURN_REPLENISHMENT_PAYMENT]: 'Return Replenishment Payment',
  [IClaimPostingInfoDtoTransactionTypeEnum.RECORD_KEEPING]: 'Record Keeping',
  [IClaimPostingInfoDtoTransactionTypeEnum.CONTRIBUTION_PAID]: 'Contribution Paid',
  [IClaimPostingInfoDtoTransactionTypeEnum.PREFUND_REPLENISHMENT]: 'Prefund Replenishment',
  [IClaimPostingInfoDtoTransactionTypeEnum.INTERNAL_TRANSFER_OF_ASSETS]: 'Internal Transfer of Assets',
  [IClaimPostingInfoDtoTransactionTypeEnum.HSA_INCOMING_CONTRIBUTION]: 'HSA Incoming Contribution',
  [IClaimPostingInfoDtoTransactionTypeEnum.HSA_ROLLOVER]: 'HSA Rollover',
  [IClaimPostingInfoDtoTransactionTypeEnum.ORPHAN_EE_FEE]: 'Orphan EE Fee',
  [IClaimPostingInfoDtoTransactionTypeEnum.ORPHAN_EE_FEE_PAYMENT]: 'Orphan EE Fee Payment',
  [IClaimPostingInfoDtoTransactionTypeEnum.ADMIN_FEE]: 'Admin Fee',
  [IClaimPostingInfoDtoTransactionTypeEnum.ADMIN_FEE_PAYMENT]: 'Admin Fee Payment',
  [IClaimPostingInfoDtoTransactionTypeEnum.REPLENISHMENT_REVERSE]: 'Replenishment Reverse',
  [IClaimPostingInfoDtoTransactionTypeEnum.ACH_REJECTED]: 'ACH Rejected',
  [IClaimPostingInfoDtoTransactionTypeEnum.RETURN_CONTRIBUTION_PAYMENT]: 'Return Contribution Payment',
  [IClaimPostingInfoDtoTransactionTypeEnum.HSA_INCOMING_CONTRIBUTION_REVERSE]:
    'HSA Incoming Contribution Reverse',
  [IClaimPostingInfoDtoTransactionTypeEnum.HSA_ROLLOVER_REVERSE]: 'HSA Rollover Reverse',
  [IClaimPostingInfoDtoTransactionTypeEnum.INVESTMENT_FEES]: 'Investment Fees',
  [IClaimPostingInfoDtoTransactionTypeEnum.REPLENISHMENT_FUNDING_REVERSE]: 'Replenishment Funding Reverse',
  [IClaimPostingInfoDtoTransactionTypeEnum.RETURN_REPLENISHMENT_FUNDING_PAYMENT]:
    'Return Replenishment Funding Payment',
  [IClaimPostingInfoDtoTransactionTypeEnum.CLAIM_TRANSFER]: 'Claim Transfer',
  [IClaimPostingInfoDtoTransactionTypeEnum.CLAIM_TRANSFER_INTERNAL]: 'Claim Transfer Internal',
  [IClaimPostingInfoDtoTransactionTypeEnum.PAYROLL_REVERSE]: 'Payroll Reverse',
  [IClaimPostingInfoDtoTransactionTypeEnum.CONTRIBUTION_PAID_REVERSE]: 'Contribution Paid Reverse',
  [IClaimPostingInfoDtoTransactionTypeEnum.CONTRIBUTION_REVERSE_PAYMENT]: 'Contribution Reverse Payment',
  [IClaimPostingInfoDtoTransactionTypeEnum.PREFUND_REPLENISHMENT_RETURN]: 'Prefund Replenishment Return',
  [IClaimPostingInfoDtoTransactionTypeEnum.FINAL_PREFUND_REPLENISHMENT_RETURN]:
    'Final Prefund Replenishment Return',
  [IClaimPostingInfoDtoTransactionTypeEnum.PREFUND_PAYMENT]: 'Prefund Payment',
  [IClaimPostingInfoDtoTransactionTypeEnum.PREFUND_INTER]: 'Prefund Inter',
  [IClaimPostingInfoDtoTransactionTypeEnum.RETURN_PREFUND_FUNDING_PAYMENT]: 'Return Prefund Funding Payment',
  [IClaimPostingInfoDtoTransactionTypeEnum.PREFUND_FINAL]: 'Prefund Final',
  [IClaimPostingInfoDtoTransactionTypeEnum.PREFUND_FUNDING_REVERSE]: 'Prefund Funding Reverse',
  [IClaimPostingInfoDtoTransactionTypeEnum.RETURN_LOCKBOX]: 'Return Lockbox',
  [IClaimPostingInfoDtoTransactionTypeEnum.INBOUND_CHECK_CLEARED]: 'Inbound Check Cleared',
  [IClaimPostingInfoDtoTransactionTypeEnum.LOCKBOX_INCOMING]: 'Lockbox Incoming',
  [IClaimPostingInfoDtoTransactionTypeEnum.INBOUND_TRANSFER_OF_ASSETS]: 'Inbound Transfer of Assets',
  [IClaimPostingInfoDtoTransactionTypeEnum.OUTBOUND_TRANSFER_OF_ASSETS]: 'Outbound Transfer of Assets',
  [IClaimPostingInfoDtoTransactionTypeEnum.OUTBOUND_CHECK_CLEARED]: 'Outbound Check Cleared',
  [IClaimPostingInfoDtoTransactionTypeEnum.CLAIM_OFFSET]: 'Claim Offset',
  [IClaimPostingInfoDtoTransactionTypeEnum.WRITE_OFF]: 'Write Off',
  [IClaimPostingInfoDtoTransactionTypeEnum.FORFEITURE]: 'Forfeiture',
  [IClaimPostingInfoDtoTransactionTypeEnum.PUSH_REPLENISHMENT_PAYMENT]: 'Push Replenishment Payment',
  [IClaimPostingInfoDtoTransactionTypeEnum.PUSH_PREFUND_PAYMENT]: 'Push Prefund Payment',
  [IClaimPostingInfoDtoTransactionTypeEnum.PUSH_CONTRIBUTION_PAYMENT]: 'Push Contribution Payment',
  [IClaimPostingInfoDtoTransactionTypeEnum.PAYMENT_EE_FEE]: 'Payment EE Fee',
  [IClaimPostingInfoDtoTransactionTypeEnum.ACCOUNT_MAINTENANCE_EE_FEE]: 'Account Maintenance EE Fee',
  [IClaimPostingInfoDtoTransactionTypeEnum.PAPER_STATEMENT_EE_FEE]: 'Paper Statement EE Fee',
  [IClaimPostingInfoDtoTransactionTypeEnum.EE_FEES_BULK_PAYMENT]: 'EE Fees Bulk Payment',
  [IClaimPostingInfoDtoTransactionTypeEnum.OTHER_ROOT_INTERNAL_TRANSFER_OF_ASSETS]:
    'Other Root Internal Transfer of Assets',
  [IClaimPostingInfoDtoTransactionTypeEnum.FEE_TRANSFER_INTERNAL]: 'Fee Transfer Internal',
  [IClaimPostingInfoDtoTransactionTypeEnum.FEE_TRANSFER]: 'Fee Transfer',
  [IClaimPostingInfoDtoTransactionTypeEnum.CLAIM_REJECTION]: 'Claim Rejection',
  [IClaimPostingInfoDtoTransactionTypeEnum.ACH_CLAIM_REPAYMENT]: 'ACH Claim Repayment',
  [IClaimPostingInfoDtoTransactionTypeEnum.ACH_CLAIM_REPAYMENT_REJECTED]: 'ACH Claim Repayment Rejected',
  [IClaimPostingInfoDtoTransactionTypeEnum.CLAIM_REPAYMENT_REJECTION]: 'Claim Repayment Rejection',
  [IClaimPostingInfoDtoTransactionTypeEnum.CLAIM_REPAYMENT_TRANSFER_INTERNAL]:
    'Claim Repayment Transfer Internal',
  [IClaimPostingInfoDtoTransactionTypeEnum.CLAIM_REPAYMENT_TRANSFER]: 'Claim Repayment Transfer',
  [IClaimPostingInfoDtoTransactionTypeEnum.LOCKBOX_CHECK_CLEARED]: 'Lockbox Check Cleared',
  [IClaimPostingInfoDtoTransactionTypeEnum.LOCKBOX_CLAIM_REPAYMENT]: 'Lockbox Claim Repayment',
  [IClaimPostingInfoDtoTransactionTypeEnum.NOTIONAL_CONTRIBUTION_PAID]: 'Notional Contribution Paid',
  [IClaimPostingInfoDtoTransactionTypeEnum.PREFUND_RETURN_PAYMENT_REVERSE]: 'Prefund Return Payment Reverse',
  [IClaimPostingInfoDtoTransactionTypeEnum.CLAIM_BALANCE_ADJUSTMENT]: 'Claim Balance Adjustment',
  [IClaimPostingInfoDtoTransactionTypeEnum.NOTIONAL_CONTRIBUTION_PAID_REVERSE]:
    'Notional Contribution Paid Reverse',
  [IClaimPostingInfoDtoTransactionTypeEnum.ROLLOVER_BALANCE_ADJUSTMENT]: 'Rollover Balance Adjustment',
  [IClaimPostingInfoDtoTransactionTypeEnum.EOY_PLAN_FUNDS_INTERNAL_RETURN]: 'EOY Plan Funds Internal Return',
  [IClaimPostingInfoDtoTransactionTypeEnum.PUSH_RETURN_PAYMENT]: 'Push Return Payment',
  [IClaimPostingInfoDtoTransactionTypeEnum.HSA_INCOMING_CONTRIBUTION_PAYMENT]:
    'HSA Incoming Contribution Payment',
  [IClaimPostingInfoDtoTransactionTypeEnum.NOTIONAL_PAYROLL_REVERSE]: 'Notional Payroll Reverse',
  [IClaimPostingInfoDtoTransactionTypeEnum.EOY_PLAN_FUNDS_INTERNAL_RETURN_PAYMENT]:
    'EOY Plan Funds Internal Return Payment',
  [IClaimPostingInfoDtoTransactionTypeEnum.EOY_PLAN_FUNDS_EXTERNAL_RETURN]: 'EOY Plan Funds External Return',
  [IClaimPostingInfoDtoTransactionTypeEnum.EOY_PLAN_FUNDS_EXTERNAL_RETURN_PAYMENT]:
    'EOY Plan Funds External Return Payment',
  [IClaimPostingInfoDtoTransactionTypeEnum.EOY_PLAN_FUNDS_EXTERNAL_RETURN_PAYMENT_REVERSE]:
    'EOY Plan Funds External Return Payment Reverse',
  [IClaimPostingInfoDtoTransactionTypeEnum.EOY_PLAN_SHORTAGE_COLLECTION]: 'EOY Plan Shortage Collection',
  [IClaimPostingInfoDtoTransactionTypeEnum.EOY_PLAN_SHORTAGE_COLLECTION_PULL_PAYMENT]:
    'EOY Plan Shortage Collection Payment',
  [IClaimPostingInfoDtoTransactionTypeEnum.EOY_PLAN_SHORTAGE_COLLECTION_PUSH_PAYMENT]:
    'EOY Plan Shortage Collection Payment',
  [IClaimPostingInfoDtoTransactionTypeEnum.EOY_PLAN_SHORTAGE_COLLECTION_PULL_PAYMENT_REVERSE]:
    'EOY Plan Shortage Collection Payment Reverse',
  [IClaimPostingInfoDtoTransactionTypeEnum.PUSH_UNKNOWN_PAYMENT]: 'Push Unknown Payment',
  [IClaimPostingInfoDtoTransactionTypeEnum.UNMATCHED_PUSH_KEEPING]: 'Unmatched Push Keeping',
  [IClaimPostingInfoDtoTransactionTypeEnum.PUSH_INVOICE_PAYMENT]: 'Push Invoice Payment',
  [IClaimPostingInfoDtoTransactionTypeEnum.UNMATCHED_PUSH_RETURN]: 'Unmatched Push Return',
  [IClaimPostingInfoDtoTransactionTypeEnum.CLAIM_BALANCE_ADJUSTMENT_EXTERNAL]:
    'Claim Balance Adjustment',
  [IClaimPostingInfoDtoTransactionTypeEnum.ROLLOVER_BALANCE_ADJUSTMENT_CROSS_OMNIBUS]:
    'Rollover Balance Adjustment',
  [IClaimPostingInfoDtoTransactionTypeEnum.ROLLOVER_BALANCE_ADJUSTMENT_EXTERNAL]:
    'Rollover Balance Adjustment',
  [IClaimPostingInfoDtoTransactionTypeEnum.BULK_BALANCE_ADJUSTMENT]: 'Bulk Balance Adjustment',
  [IClaimPostingInfoDtoTransactionTypeEnum.BULK_BALANCE_ADJUSTMENT_PULL_PAYMENT]:
    'Bulk Balance Adjustment Pull Payment',
  [IClaimPostingInfoDtoTransactionTypeEnum.BULK_BALANCE_ADJUSTMENT_PUSH_PAYMENT]:
    'Bulk Balance Adjustment Push Payment',
  [IClaimPostingInfoDtoTransactionTypeEnum.BULK_BALANCE_ADJUSTMENT_PULL_PAYMENT_REVERSE]:
    'Bulk Balance Adjustment Pull Payment Reverse',
  [IClaimPostingInfoDtoTransactionTypeEnum.BULK_BALANCE_ADJUSTMENT_REVERSE]:
    'Bulk Balance Adjustment Reverse',
  [IClaimPostingInfoDtoTransactionTypeEnum.CLAIM_BALANCE_ADJUSTMENT_EXTERNAL_REVERSE]:
    'Claim Balance Adjustment',
  [IClaimPostingInfoDtoTransactionTypeEnum.ROLLOVER_BALANCE_ADJUSTMENT_EXTERNAL_REVERSE]:
    'Rollover Balance Adjustment',
  [IClaimPostingInfoDtoTransactionTypeEnum.BULK_BALANCE_ADJUSTMENT_RETURN_PAYMENT]:
    'Bulk Balance Adjustment Return Payment',
  [IClaimPostingInfoDtoTransactionTypeEnum.BULK_BALANCE_ADJUSTMENT_RETURN_PAYMENT_REVERSE]:
    'Bulk Balance Adjustment Return Payment Reverse',
};

export enum TransactionApprovalCode {
  PAYMENT_REJECTED = 'PAYMENT_REJECTED',

  EXPENSE_NOT_ELIGIBLE_PER_IRS_RULES = 'EXPENSE_NOT_ELIGIBLE_PER_IRS_RULES',
  EXPENSE_NOT_ELIGIBLE_PER_PLAN_RULES = 'EXPENSE_NOT_ELIGIBLE_PER_PLAN_RULES',
  SERVICE_DATE_OUTSIDE_OF_COVERAGE_PERIOD = 'SERVICE_DATE_OUTSIDE_OF_COVERAGE_PERIOD',
  REQUESTED_AMOUNT_IN_EXCESS_OF_ALLOWED_AMOUNT = 'REQUESTED_AMOUNT_IN_EXCESS_OF_ALLOWED_AMOUNT',
  DETAILED_RECEIPT_REQUIRED = 'DETAILED_RECEIPT_REQUIRED',
  PROOF_OF_EXPENSE_NEEDED = 'PROOF_OF_EXPENSE_NEEDED',
  ADDITIONAL_DOCUMENTATION_NEEDED_TO_SUBSTANTIATE_TRANSACTION = 'ADDITIONAL_DOCUMENTATION_NEEDED_TO_SUBSTANTIATE_TRANSACTION',
  LETTER_OF_MEDICAL_NECESSITY_REQUIRED = 'LETTER_OF_MEDICAL_NECESSITY_REQUIRED',
  CLAIM_RECEIVED_AFTER_RUNOUT_DATE = 'CLAIM_RECEIVED_AFTER_RUNOUT_DATE',
  CLAIM_SUBMITTED_AFTER_GRACE_PERIOD = 'CLAIM_SUBMITTED_AFTER_GRACE_PERIOD',
  DUPLICATE_CLAIM = 'DUPLICATE_CLAIM',
  CUSTOM = 'CUSTOM',

  WAITING_FOR_FUNDS = 'WAITING_FOR_FUNDS',
  PARTIALLY_PAID = 'PARTIALLY_PAID',
  PAID_INFO = 'PAID_INFO',
  DECLINED = 'DECLINED',
  WAITING_FOR_FUNDS_NO_PAID = 'WAITING_FOR_FUNDS_NO_PAID',
  WAITING_FOR_FUNDS_PAID = 'WAITING_FOR_FUNDS_PAID',
  WAITING_FOR_FUNDS_PARTIALLY_APPROVED_PARTIALLY_PAID = 'WAITING_FOR_FUNDS_PARTIALLY_APPROVED_PARTIALLY_PAID',
  WAITING_FOR_FUNDS_APPROVED_PARTIALLY_PAID = 'WAITING_FOR_FUNDS_APPROVED_PARTIALLY_PAID',
  CARD_SWIPE_DECLINED_BY_CARD_PROCESSOR = 'CARD_SWIPE_DECLINED_BY_CARD_PROCESSOR',
  EXPENSE_NOT_ELIGIBLE_PER_IRS_RULES_PARTIALLY = 'EXPENSE_NOT_ELIGIBLE_PER_IRS_RULES_PARTIALLY',
  EXPENSE_NOT_ELIGIBLE_PER_PLAN_RULES_PARTIALLY = 'EXPENSE_NOT_ELIGIBLE_PER_PLAN_RULES_PARTIALLY',
  SERVICE_DATE_OUTSIDE_OF_COVERAGE_PERIOD_PARTIALLY = 'SERVICE_DATE_OUTSIDE_OF_COVERAGE_PERIOD_PARTIALLY',
  DUPLICATE_CLAIM_PARTIALLY = 'DUPLICATE_CLAIM_PARTIALLY',
  REQUESTED_AMOUNT_IN_EXCESS_OF_ALLOWED_AMOUNT_PARTIALLY = 'REQUESTED_AMOUNT_IN_EXCESS_OF_ALLOWED_AMOUNT_PARTIALLY',
  ADDITIONAL_DOCUMENTATION_NEEDED_TO_SUBSTANTIATE_TRANSACTION_PARTIALLY = 'ADDITIONAL_DOCUMENTATION_NEEDED_TO_SUBSTANTIATE_TRANSACTION_PARTIALLY',
  PROOF_OF_EXPENSE_NEEDED_PARTIALLY = 'PROOF_OF_EXPENSE_NEEDED_PARTIALLY',
  LETTER_OF_MEDICAL_NECESSITY_REQUIRED_PARTIALLY = 'LETTER_OF_MEDICAL_NECESSITY_REQUIRED_PARTIALLY',
  CUSTOM_PARTIALLY = 'CUSTOM_PARTIALLY',
  OUT_OF_POCKET = 'OUT_OF_POCKET',
  APPLIED_TO_DEDUCTIBLE = 'APPLIED_TO_DEDUCTIBLE',
  AUTOMATICALLY_APPROVED_BY_CARD_PROCESSOR_IIAS = 'AUTOMATICALLY_APPROVED_BY_CARD_PROCESSOR_IIAS',
  AUTOMATICALLY_APPROVED_COPAY_MATCH = 'AUTOMATICALLY_APPROVED_COPAY_MATCH',
  AUTOMATICALLY_APPROVED_BY_CARD_PROCESSOR_COPAY_MATCH = 'AUTOMATICALLY_APPROVED_BY_CARD_PROCESSOR_COPAY_MATCH',
  AUTOMATICALLY_APPROVED_BY_CARD_PROCESSOR_TRUSTED_MERCHANT = 'AUTOMATICALLY_APPROVED_BY_CARD_PROCESSOR_TRUSTED_MERCHANT',
  CLAIM_COULD_NOT_BE_AUTO_SUBSTANTIATED_BY_CARD_PROCESSOR = 'CLAIM_COULD_NOT_BE_AUTO_SUBSTANTIATED_BY_CARD_PROCESSOR',
  AUTOMATICALLY_APPROVED_BY_CARD_PROCESSOR_CARRIER_CLAIM = 'AUTOMATICALLY_APPROVED_BY_CARD_PROCESSOR_CARRIER_CLAIM',
  AUTOMATICALLY_APPROVED_BY_CARD_PROCESSOR_RECURRING_CLAIM = 'AUTOMATICALLY_APPROVED_BY_CARD_PROCESSOR_RECURRING_CLAIM',
  AUTOMATICALLY_APPROVED_BY_CARD_PROCESSOR_SUBSTANTIATION_NOT_REQUIRED = 'AUTOMATICALLY_APPROVED_BY_CARD_PROCESSOR_SUBSTANTIATION_NOT_REQUIRED',
  AUTOMATICALLY_APPROVED_NO_RECEIPT_REQUIRED = 'AUTOMATICALLY_APPROVED_NO_RECEIPT_REQUIRED',
  AUTOMATICALLY_APPROVED_CARRIER_CLAIM = 'AUTOMATICALLY_APPROVED_CARRIER_CLAIM',
  CARRIER_CLAIM = 'CARRIER_CLAIM',
  REPAYMENT_REJECTED = 'REPAYMENT_REJECTED',
}

export enum ClaimNoteGroup {
  Warning = 'WARN',
  Information = 'INFO',
}
export enum ClaimNoteType {
  NO_PAY_OUT_NOTE = 'NO_PAY_OUT_NOTE',
  SPEND_LIMIT_NOTE = 'SPEND_LIMIT_NOTE',
  CHECK_EXPIRED_NOTE = 'CHECK_EXPIRED_NOTE',
  CHECK_VOIDED_NOTE = 'CHECK_VOIDED_NOTE',
  WAITING_FOR_FUNDS_EXPIRED = 'WAITING_FOR_FUNDS_EXPIRED',
  ELECTION_LIMIT_NOTE = 'ELECTION_LIMIT_NOTE',
}
export interface ClaimNotePayload {
  claimNoteGroup: ClaimNoteGroup;
  claimNoteType: ClaimNoteType;
  createdAt: string;
}
export interface ClaimNote {
  claimNoteGroup: ClaimNoteGroup;
  claimNoteType: ClaimNoteType;
  createdAt: string;
  date: string;
}

export interface OffsetRepaymentPayload {
  donor_claim_id: number;
  recipient_claim_id: number;
  amount: number;
  created_at: string;
}

export interface OffsetRepaymentDto {
  donorClaimId: string;
  recipientClaimId: string;
  amount: number;
  date: string;
}

export enum TransactionActivityType {
  Paid = 'Paid',
  Used = 'Used',
  Success = 'Success',
  UploadReceipt = 'UploadReceipt',
  Appeal = 'Appeal',
}

export enum ProcessingStatusType {
  Submitted = 'SUBMITTED',
  Paid = 'PAID',
  PartiallyPaid = 'PARTIALLY_PAID',
  Processing = 'PROCESSING',
  PartiallyRepaid = 'PARTIALLY_REPAID',
  Repaid = 'REPAID',
  WaitingForFunds = 'WAITING_FOR_FUNDS',
  Authorized = 'AUTHORIZED',
  Declined = 'DECLINED',
  Expired = 'EXPIRED',
  DuplicateTracking = 'DEDUCTIBLE_TRACKING',
  ActionRequired = 'ACTION_REQUIRED',
  StopProcessing = 'STOP_PROCESSING',
  RepaymentRequired = 'REPAYMENT_REQUIRED',
  Appealed = 'APPEALED',
}

export interface Activity {
  id: string;
  type: TransactionType;
  person?: string;
  status: TransactionStatus;
  text: string;
  amount: number;
  date: Date;
  description?: string;
}

export interface ExpenseOption {
  key: string;
  title: string;
  value: number;
  amount?: number;
}

export interface Expense {
  category_type?: string;
  percent: number;
  description: string;
  amount: number;
}

export interface ExpensesStatisticsDto {
  total_expenses: number;
  expenses?: Expense[];
}

export interface ActivityDetailsParams {
  keys?: string;
  dates?: string;
}

export interface TransactionActivity {
  id: string;
  type: TransactionActivityType;
  datetime: Date;
  text: string;
  money?: number;
}

export interface ExpandedActivity extends Activity {
  statuses: Status[];
  activities: TransactionActivity[];
}

export interface MyAccount {
  key: string;
  type: string;
  navigationTitle: string;
  title: string;
  description?: string;
  balance?: number;
  election?: number;
  expenses: ExpenseOption[];
}

export interface SearchTransactionParams {
  category_types?: string;
  statuses?: string;
  provider?: string;
  status_date_from?: string | null;
  status_date_to?: string | null;
  accountTypes?: string;
  years?: string;
  page?: number;

  size?: number;
  employee_id?: string;
}

export interface Election {
  id: number;
  policy_id: number;
  amount: number;
  valid_from: Date;
  valid_to: Date;
}

export interface PlanYear {
  id: number;
  organization_id: number;
  name: string;
  valid_from: Date;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  valid_to: any;
}

export interface Plan {
  id: number;
  plan_year_id?: number;
  name: string;
  name_state: string;
  plan_code: string;
  plan_code_state: string;
  prior_plan_id: string;
  prior_plan_id_state: string;
  account_type: string;
  account_type_state: string;
  carded: boolean;
  carded_state: string;
  cip_required: boolean;
  cip_required_state: string;
  parent_id?: number;
  is_plan: boolean;
  plan_year: PlanYear;
}

export interface ExpensesDetailsParams {
  employee_id?: number | string;
  account_id?: number;
  category_type?: string;
  date_from?: Date | string;
  date_to?: Date | string;
}

export interface TransactionDto {
  category_type: ActivityType;
  paid_amount: number;
  provider_name: string;
  requested_amount: number;
  status: string;
  status_date: Date | string;
  claim_id: string;
  category_id: number;
}

export interface Transaction {
  categoryType: ActivityType;
  paidAmount: number;
  provider: string;
  requestedAmount: number;
  status: string;
  statusDate: Date | string;
  claimId: string;
}

export interface NotificationResponse {
  invoice_posting_id: number;
  approval_code: TransactionApprovalCode;
  status?: TransactionStatus;
  valid_from: Date | string;
  valid_to?: Date | string;
  custom_denial_reason?: string;
}

export interface StatusResponse {
  claim_id: number;
  status: TransactionStatus;
  id: number;
  valid_from: string;
  valid_to: string;
}

export interface TransactionPostingsResponse {
  account_id?: number;
  plan_name?: string;
  amount?: number;
  transaction_at: Date | string;
}

export interface ClaimTransferPayload {
  id?: number;
  claim_id?: number;
  account_from_id?: number;
  account_from_name?: string;
  account_to_id?: number;
  account_to_name?: string;
  posting_id_from?: number;
  posting_id_to?: number;
  created_by_contact_id?: number;
  created_at?: string;
  amount?: number;
}

export interface ServicePayload {
  id: number;
  category_type: string;
  displayed: boolean;
  name: string;
}

export interface TransactionServiceForResponse {
  relationship_type?: string;
  name: string;
  is_hidden?: boolean;
  is_view_name_allowed?: boolean;
  birth_on?: string;
}

export enum FeeFlowType {
  REVERSAL = 'REVERSAL',
  TRANSFER = 'TRANSFER',
}

export interface FeeModificationPayload {
  id?: number;
  fee_flow_type?: FeeFlowType;
  claim_id?: number;
  amount?: number;
  paid_fee_account_id?: number;
  paid_fee_plan_name?: string;
  returned_fee_amount_account_id?: number;
  returned_fee_amount_plan_name?: string;
  transaction_id?: number;
  transaction_at: string;
  next_id?: number;
  original_fee_id?: number;
  partner_organization_id?: number;
  fee_type?: string;
  payment_fee_type?: string;
}
export interface FeeModification {
  id?: string;
  feeFlowType?: FeeFlowType;
  claimId?: string;
  amount?: string;
  paidFeeAccountId?: string;
  paidFeePlanName?: string;
  returnedFeeAmountAccountId?: string;
  returnedFeeAmountPlanName?: string;
  transactionId?: string;
  transactionAt?: string;
  date: string;
  nextId?: string;
  originalFeeId?: string;
  partnerOrganizationId?: string;
  feeType?: string;
  paymentFeeType?: string;
}

export interface PaymentFeePayload {
  amount?: number;
  account_name?: string;
  account_id?: number;
  transaction_at?: string;
  payment_fee_type?: string;
  fee_type?: string;
  fee_id?: number;
}

export interface PaymentFee {
  amount?: string;
  accountName?: string;
  accountId?: string;
  transactionAt?: string;
  date: string;
  feeType?: string;
  paymentFeeType?: string;
  claimId?: string;
  feeId?: string;
  feeModificationId?: string;
}

export enum PreFormattedActivityType {
  FEE = 'FEE',
  FEE_MODIFICATION = 'FEE_MODIFICATION',
  CLAIM_REJECTION_CLAIM_POSTING = 'CLAIM_REJECTION_CLAIM_POSTING',
}
export enum SectionStyleType {
  DEFAULT = 'DEFAULT',
  ERROR = 'ERROR',
  SUCCESS = 'SUCCESS',
  WARNING = 'WARNING',
  INFO = 'INFO',
}

export enum BorderStyleType {
  DEFAULT = 'DEFAULT',
  INFO = 'INFO',
  ERROR = 'ERROR',
  WARNING = 'WARN',
}
export interface PreFormattedData {
  preFormattedActivityType?: PreFormattedActivityType;
  htmlText?: string;
  date: string;
  sectionStyleType?: SectionStyleType;
  borderStyleType?: BorderStyleType;
}
export interface PreFormattedDataPayload {
  activity_type?: PreFormattedActivityType;
  text?: string;
  timestamp: string;
  section_style_type: SectionStyleType;
  border_style_type: BorderStyleType;
}

export interface TransactionResponse {
  document_id: number;
  amount: number;
  paid_amount: number;
  approved_amount: number;
  category_type: string;
  claim_id?: number | string;
  created_at: Date | string | number;
  local_date_service_on: string;
  notifications?: NotificationResponse[];
  claim_note_dto_list?: ClaimNotePayload[];
  claim_offset_dtos?: OffsetRepaymentPayload[];
  payment_fees?: PaymentFeePayload[];
  fee_modifications?: FeeModificationPayload[];
  activity_details_v2?: PreFormattedDataPayload[];
  activity_details_v3?: PreFormattedDataPayload[];
  status_details?: PreFormattedDataPayload[];
  memo: string;
  provider_id?: number;
  service: ServicePayload;
  service_for: TransactionServiceForResponse;
  statuses?: StatusResponse[];
  payment_type: PaymentType;
  provider_invoice?: string;
  postings: TransactionPostingsResponse[];
  claim_transfers: ClaimTransferPayload[];
  transaction_type?: TransactionType;
  processing_status?: ProcessingStatusType;
  processing_substatus_type?: TransactionApprovalCode;
  apply_deductible_amount?: number | null;
  payment_method_id?: string;
  tax_year?: string;
  employee_notes?: string;
  letter_of_medical_necessities: ILetterOfMedicalNecessityDto[];
}

export interface Notification {
  approvalCode?: TransactionApprovalCode;
  amount?: number | null;
  invoicePostingId?: string;
  status?: TransactionStatus;
  validFrom: string;
  date: string;
  validTo?: string;
  customDenialReason?: string;
}

export interface TransactionNotificationFormatted {
  accountId?: number | null;
  approvalCode?: string;
  accountName?: string | null;
  amount?: number | null;
  status?: TransactionStatus;
  validFrom?: Date | string;
  validTo?: Date | string;
  customDenialReason?: string;
}

export interface Status {
  status: TransactionStatus;
  validFrom: string;
  date: string;
  validTo: Date | string;
}

export interface TransactionPostingsData {
  accountId?: string;
  planName?: string;
  amount?: number;
  transactionAt: string;
  date: string;
}

export interface ClaimTransferData {
  id?: string;
  claimId?: string;
  accountFromId?: string;
  accountFromName?: string;
  accountToId?: string;
  accountToName?: string;
  postingIdFrom?: string;
  postingIdTo?: string;
  createdByContactId?: string;
  createdAt?: string;
  amount?: number;
  date: string;
}

export enum MessageType {
  notification = 'notification',
  posting = 'posting',
  claimNote = 'claimNote',
  successStatus = 'successStatus',
}
interface SortingMapData {
  date: string;
  index: number;
  type: MessageType;
}

export interface TransactionActivities {
  notifications?: Notification[];
  postings?: TransactionPostingsData[];
  claimNoteList?: ClaimNote[];
  claimOffsetDonors?: OffsetRepaymentDto[];
  claimOffsetRecipients?: OffsetRepaymentDto[];
  successStatuses?: Status[];
  sortingMapData: SortingMapData[];
  statuses?: Status[];
  currentStatus: TransactionStatus;
}

export interface TransactionActivitiesData {
  [key: string]: TransactionActivities;
}

export enum ApprovalStatus {
  DOCS_NEEDED = 'Docs needed',
}

export interface Service {
  id: string;
  categoryType: string;
  displayed: boolean;
  name: string;
}

export interface TransactionServiceForData {
  relationshipType?: string;
  name: string;
  isHidden?: boolean;
  isViewNameAllowed?: boolean;
  birthOn?: string;
}

interface LetterOfMedicalNecessity {
  id?: string;
  documentId?: string;
}

export interface TransactionData {
  approvalStatus?: ApprovalStatus | string;
  documentId?: string;
  claimId?: string;
  amount: number;
  paidAmount?: number;
  approvedAmount?: number;
  category: ActivityType;
  id?: number;
  createdAt?: string;
  serviceOn?: string;
  invoiceId?: number;
  notifications: Notification[];
  memo: string;
  providerId?: string;
  service?: Service;
  serviceFor: TransactionServiceForData;
  statuses: Status[];
  paymentType: PaymentType;
  postings: TransactionPostingsData[];
  paymentFees?: PaymentFee[];
  feeModifications?: FeeModification[];
  preFormattedActivityDetails?: PreFormattedData[];
  claimTransfers?: ClaimTransferData[];
  transactionType?: TransactionType;
  processingStatus?: ProcessingStatusType;
  processingSubstatusType?: TransactionApprovalCode;
  applyDeductibleAmount?: number | null;
  claimNoteList: ClaimNote[];
  claimOffsetDonors?: OffsetRepaymentDto[];
  paymentMethodId?: string;
  taxYear?: string;
  statusDate?: string;
  employeeNotes?: string;
  letterOfMedicalNecessities?: LetterOfMedicalNecessity[];
}

export interface Transactor {
  providerName: string;
  providerAddress1: string;
  providerAddress2: string;
  providerCity: string;
  providerState: string;
  providerZipCode: string;
}

export interface TransactorResponse {
  name: string;
  address?: AddressResponse;
}

export interface AddressResponse {
  city?: string;
  line1?: string;
  line2?: string;
  state?: StateResponse;
  zipcode?: string;
}

export interface StateResponse {
  code?: string;
  id?: number;
  name?: string;
}

export interface RelationshipResponse {
  id: number;
  name: string;
}

export interface Relationship {
  id: string;
  name: string;
  value: string;
}

export enum NotificationStatusColorType {
  SUCCESS = 'Success',
  WARNING = 'Warning',
  ERROR = 'Error',
  SUCCESS_LIGHT = 'SuccessLight',
  WARNING_LIGHT = 'WarningLight',
  ERROR_LIGHT = 'ErrorLight',
  DEFAULT = 'Default',
}
